"use client"

import { useState, useEffect } from "react"
import { Card } from "../ui/card"
import { cn } from "../lib/utils"
import Image from "next/image"

interface RoleSelectionProps {
  onSelect: (role: "parent" | "child") => void
}

export function RoleSelection({ onSelect }: RoleSelectionProps) {
  const [parentImage, setParentImage] = useState<string | null>(null)
  const [childImage, setChildImage] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Skip image generation - just show default UI
    setLoading(false)
    setParentImage(null)
    setChildImage(null)
  }, [])

  return (
    <div className="flex gap-4 justify-center flex-wrap px-4 py-6">
      <Card
        className={cn(
          "cursor-pointer transition-all hover:scale-105 hover:shadow-lg",
          "w-full sm:w-[280px] p-4 border-2 hover:border-primary"
        )}
        onClick={() => onSelect("parent")}
      >
        <div className="flex flex-col items-center gap-3">
          {loading ? (
            <div className="w-full h-48 bg-muted animate-pulse rounded-lg" />
          ) : (
            <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted">
              {parentImage ? (
                parentImage.startsWith('data:image') ? (
                  <img
                    src={parentImage}
                    alt="Parent"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image
                    src={parentImage}
                    alt="Parent"
                    fill
                    className="object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  Parent
                </div>
              )}
            </div>
          )}
          <h3 className="text-lg font-semibold">I'm a Parent</h3>
          <p className="text-sm text-muted-foreground text-center">
            Get information and support for your child's care
          </p>
        </div>
      </Card>

      <Card
        className={cn(
          "cursor-pointer transition-all hover:scale-105 hover:shadow-lg",
          "w-full sm:w-[280px] p-4 border-2 hover:border-primary"
        )}
        onClick={() => onSelect("child")}
      >
        <div className="flex flex-col items-center gap-3">
          {loading ? (
            <div className="w-full h-48 bg-muted animate-pulse rounded-lg" />
          ) : (
            <div className="relative w-full h-48 rounded-lg overflow-hidden bg-muted">
              {childImage ? (
                childImage.startsWith('data:image') ? (
                  <img
                    src={childImage}
                    alt="Child"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image
                    src={childImage}
                    alt="Child"
                    fill
                    className="object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  Child
                </div>
              )}
            </div>
          )}
          <h3 className="text-lg font-semibold">I'm a Child</h3>
          <p className="text-sm text-muted-foreground text-center">
            Ask questions in simple, friendly language
          </p>
        </div>
      </Card>
    </div>
  )
}


